import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { catchError, concatMap, EMPTY, filter, forkJoin, from, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';

import { RpmApiService } from '@core/data/api/rpm-api.service';
import {
  grassItemFromDto,
  rpmCategoryFromDto,
  rpmCategoryToDto,
  rpmItemFromDto,
  rpmItemToDto,
  rpmNoteFromDto,
  rpmReviewFromDto,
  rpmSummaryFromDto,
  rpmWeekFromDto,
} from '@core/data/api/rpm.mapper';
import { RpmPersonData } from '@core/models';
import { apiErrorMessage } from '@core/util/api-error.util';
import { rpmToday, rpmWeekStart } from '@core/util/rpm.util';
import { selectCurrentPersonaId } from '@state/session/session.reducer';
import { RpmApiActions, RpmPageActions } from './rpm.actions';
import { selectRpmData } from './rpm.reducer';

/**
 * RPM effects: the page load, the week navigator's read, and one effect per
 * write — each the ONE call its user action makes — answered with what the call
 * changed (`core/data/api/rpm-api.service.ts`).
 *
 * THE SERVER IS THE ONLY SOURCE. When the RPM API was wired its seed, the local
 * book that played the endpoints, and the repository methods behind them were
 * deleted (standards §8c), as Weekly Schedule's were. With no `apiUrl`
 * configured the load answers an EMPTY plan rather than invented rows, and the
 * RPM screen says it needs the server instead of offering controls that cannot
 * save (see `RpmPlanStore.serverConfigured`).
 *
 * Writes run in order (`concatMap`): a tick sent straight after an edit must
 * land on the edited block, not race it.
 */
@Injectable()
export class RpmEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly api = inject(RpmApiService);

  /**
   * On entering the RPM Plan or the MRP (Me › MRP, or My Packet's MRP tab): the live plan (with its categories), the
   * Grass Catcher, the current week and this year's summary, for the current
   * persona — in parallel, as one load.
   *
   * It waits for the persona: on a cold load the screen can open before the
   * signed-in person has been resolved, and a read addressed to nobody would be
   * a 404 about something the user never asked for.
   */
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RpmPageActions.entered),
      switchMap(() =>
        this.store.select(selectCurrentPersonaId).pipe(
          filter((id): id is string => !!id),
          take(1),
        ),
      ),
      switchMap((personaId) =>
        this.book$(personaId).pipe(
          map((data) => RpmApiActions.loadSuccess({ personaId, data })),
          catchError((err: unknown) => of(RpmApiActions.loadFailure({ error: apiErrorMessage(err, 'The RPM Plan could not be read.') }))),
        ),
      ),
    ),
  );

  /** The week navigator moved: read that week. Latest wins, so paging quickly shows the last week asked for. */
  readonly openWeek$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RpmPageActions.weekOpened),
      switchMap(({ personaId, weekStartDt }) =>
        this.api.readWeek(personaId, weekStartDt).pipe(
          map((dto) => RpmApiActions.weekLoaded({ personaId, week: rpmWeekFromDto(dto) })),
          // The shared dialog has shown why; the navigator keeps the last week it had.
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  /**
   * After a write that changes what the server DERIVES — a week's outcomes and
   * counts, the year summary — read those two again. A READ, not a second
   * write: the write was one call and has already landed; the counts are the
   * server's arithmetic, and working them out here instead is exactly the copy
   * of the rules that wiring the API deleted. The week re-read is the one being
   * looked at, so a review saved on a past week refreshes that week.
   */
  readonly refreshDerived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RpmApiActions.itemSaved, RpmApiActions.itemRemoved, RpmApiActions.grassPromoted, RpmApiActions.reviewSaved),
      withLatestFrom(this.store.select(selectRpmData)),
      switchMap(([{ personaId }, data]) => {
        const weekStartDt = data[personaId]?.week?.weekStartDt ?? rpmWeekStart(rpmToday());
        return forkJoin({
          week: this.api.readWeek(personaId, weekStartDt),
          summary: this.api.readSummary(personaId, Number(rpmToday().slice(0, 4))),
        }).pipe(
          concatMap(({ week, summary }) =>
            from([
              RpmApiActions.weekLoaded({ personaId, week: rpmWeekFromDto(week) }),
              RpmApiActions.summaryLoaded({ personaId, summary: rpmSummaryFromDto(summary) }),
            ]),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  readonly addItem$ = this.write(RpmPageActions.itemAdded, (a) =>
    this.api.addItem(a.personaId, rpmItemToDto(a.body)).pipe(map((dto) => RpmApiActions.itemSaved({ personaId: a.personaId, item: rpmItemFromDto(dto) }))),
  );

  readonly editItem$ = this.write(RpmPageActions.itemEdited, (a) =>
    this.api
      .saveItem(a.personaId, a.itemId, rpmItemToDto(a.body))
      .pipe(map((dto) => RpmApiActions.itemSaved({ personaId: a.personaId, item: rpmItemFromDto(dto) }))),
  );

  readonly deleteItem$ = this.write(RpmPageActions.itemDeleted, (a) =>
    this.api.deleteItem(a.personaId, a.itemId).pipe(map(() => RpmApiActions.itemRemoved({ personaId: a.personaId, itemId: a.itemId }))),
  );

  readonly setStatus$ = this.write(RpmPageActions.statusSet, (a) =>
    this.api
      .setStatus(a.personaId, a.itemId, { rpmItemStatus: a.status })
      .pipe(map((dto) => RpmApiActions.itemSaved({ personaId: a.personaId, item: rpmItemFromDto(dto) }))),
  );

  readonly tickAction$ = this.write(RpmPageActions.actionTicked, (a) =>
    this.api
      .setActionComplete(a.personaId, a.itemId, a.actionId, { complete: a.complete })
      .pipe(map((dto) => RpmApiActions.itemSaved({ personaId: a.personaId, item: rpmItemFromDto(dto) }))),
  );

  readonly reorder$ = this.write(RpmPageActions.planReordered, (a) =>
    this.api.reorder(a.personaId, { rpmItemGuids: a.itemIds }).pipe(
      map((dto) =>
        RpmApiActions.planOrdered({
          personaId: a.personaId,
          items: (dto.rpmItems ?? []).map(rpmItemFromDto),
          categories: (dto.categories ?? []).map(rpmCategoryFromDto),
        }),
      ),
    ),
  );

  readonly addNote$ = this.write(RpmPageActions.noteAdded, (a) =>
    this.api
      .addNote(a.personaId, a.itemId, { text: a.text })
      .pipe(map((dto) => RpmApiActions.noteSaved({ personaId: a.personaId, itemId: a.itemId, note: rpmNoteFromDto(dto) }))),
  );

  readonly promote$ = this.write(RpmPageActions.grassPromoted, (a) =>
    this.api
      .promoteGrassItem(a.personaId, a.grassItemId, rpmItemToDto(a.body))
      .pipe(map((dto) => RpmApiActions.grassPromoted({ personaId: a.personaId, grassItemId: a.grassItemId, item: rpmItemFromDto(dto) }))),
  );

  readonly saveReview$ = this.write(RpmPageActions.reviewSaved, (a) =>
    this.api.saveReview(a.personaId, a.weekStartDt, { accomplished: a.accomplished, learned: a.learned }).pipe(
      map((dto) =>
        RpmApiActions.reviewSaved({ personaId: a.personaId, weekStartDt: a.weekStartDt, review: dto ? rpmReviewFromDto(dto) : null }),
      ),
    ),
  );

  readonly addCategory$ = this.write(RpmPageActions.categoryAdded, (a) =>
    this.api
      .addCategory(a.personaId, rpmCategoryToDto(a.body))
      .pipe(map((dto) => RpmApiActions.categorySaved({ personaId: a.personaId, category: rpmCategoryFromDto(dto) }))),
  );

  readonly editCategory$ = this.write(RpmPageActions.categoryEdited, (a) =>
    this.api
      .saveCategory(a.personaId, a.categoryId, rpmCategoryToDto(a.body))
      .pipe(map((dto) => RpmApiActions.categorySaved({ personaId: a.personaId, category: rpmCategoryFromDto(dto) }))),
  );

  readonly capture$ = this.write(RpmPageActions.grassCaptured, (a) =>
    this.api
      .captureGrassItem(a.personaId, { text: a.text, categoryGuid: a.categoryId })
      .pipe(map((dto) => RpmApiActions.grassCaptured({ personaId: a.personaId, item: grassItemFromDto(dto) }))),
  );

  readonly deleteGrass$ = this.write(RpmPageActions.grassDeleted, (a) =>
    this.api
      .discardGrassItem(a.personaId, a.grassItemId)
      .pipe(map(() => RpmApiActions.grassDeleted({ personaId: a.personaId, grassItemId: a.grassItemId }))),
  );

  /**
   * One write effect: on `creator`, make the call and dispatch its answer, or
   * Write Failure with the server's own reason. Every write goes through here so
   * none of them can forget to end the in-flight state or report a refusal
   * (standards §8).
   */
  private write<C extends ActionCreator>(creator: C, call: (action: ReturnType<C>) => Observable<Action>) {
    return createEffect(() =>
      this.actions$.pipe(
        ofType(creator),
        concatMap((action) =>
          call(action as ReturnType<C>).pipe(
            catchError((err: unknown) => of(RpmApiActions.writeFailure({ error: apiErrorMessage(err, 'That was not saved.') }))),
          ),
        ),
      ),
    );
  }

  /**
   * One person's RPM data, read whole. Without an API it is EMPTY — never seeded
   * rows (§8c): an empty plan is the honest answer when there is nowhere to read
   * one from.
   */
  private book$(personaId: string): Observable<RpmPersonData> {
    if (!this.api.available()) return of({ items: [], categories: [], grassItems: [], week: null, summary: null });
    const today = rpmToday();
    return forkJoin({
      plan: this.api.readPlan(personaId),
      grass: this.api.readGrassCatcher(personaId),
      week: this.api.readWeek(personaId, rpmWeekStart(today)),
      summary: this.api.readSummary(personaId, Number(today.slice(0, 4))),
    }).pipe(
      map(({ plan, grass, week, summary }) => ({
        items: (plan.rpmItems ?? []).map(rpmItemFromDto),
        categories: (plan.categories ?? []).map(rpmCategoryFromDto),
        grassItems: (grass.grassCatcherItems ?? []).map(grassItemFromDto),
        week: rpmWeekFromDto(week),
        summary: rpmSummaryFromDto(summary),
      })),
    );
  }
}
